// Page "Détail facture" (Lot 3A) : récap complet + historique paiements
// + actions encaissement et édition de la note.

import { FormEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n/index";
import { clientsApi } from "../api/clients";
import { intervenantsApi } from "../api/intervenants";
import { projectsApi } from "../api/projects";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { ApiError } from "../api/client";
import { invoicesApi } from "../api/invoices";
import { companySettingsApi } from "../api/companySettings";
import { invoiceUploadsApi } from "../api/invoiceUploads";
import type {
  InvoiceDetail,
  PaymentCreatePayload,
  PaymentMethod,
  ReminderPublic,
  ReminderStatus,
} from "../api/types";

type QuotationLine = {
  id: string;
  description: string;
  quantity: string;
  unit_price: string;
  unit: string | null;
  vat_rate: string;
  amount_ht: string;
  amount_ttc: string;
  position: number;
};
import type { ClientPublic } from "../api/clients";
import type { IntervenantPublic } from "../api/intervenants";
import type { ProjectPublic } from "../api/projects";
import { AppShell } from "../components/AppShell";
import { CurrencySelect } from "../components/CurrencySelect";
import { formatAmount } from "../utils/currency";
import { DueBadge } from "../components/DueBadge";
import { FormField } from "../components/FormField";
import { Modal } from "../components/Modal";

function getMethodLabels(t: (k: string) => string): Record<PaymentMethod, string> {
  return {
    ziina: "Ziina",
    bank_transfer: t("invoiceDetail.paymentMethodBankTransfer"),
    check: t("invoiceDetail.paymentMethodCheck"),
    cash: t("invoiceDetail.paymentMethodCash"),
    card: t("invoiceDetail.paymentMethodCard"),
    other: t("invoiceDetail.paymentMethodOther"),
  };
}

// Lot 4A — pastille de statut pour une relance envoyée.
function getReminderStatusLabels(t: (k: string) => string): Record<ReminderStatus, string> {
  return {
    pending: t("invoiceDetail.reminderStatusPending"),
    sent: t("invoiceDetail.reminderStatusSent"),
    failed: t("invoiceDetail.reminderStatusFailed"),
    skipped: t("invoiceDetail.reminderStatusSkipped"),
  };
}

const REMINDER_STATUS_CLS: Record<ReminderStatus, string> = {
  pending: "bg-slate-100 text-slate-700",
  sent: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
  skipped: "bg-slate-100 text-slate-500",
};

function formatDateI18n(iso: string): string {
  const locale = i18n.language === "en" ? "en-GB" : "fr-FR";
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00Z" : ""));
  return d.toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatDateTimeI18n(iso: string): string {
  const locale = i18n.language === "en" ? "en-GB" : "fr-FR";
  const d = new Date(iso);
  return d.toLocaleString(locale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function todayISO(): string {
  const d = new Date();
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

export function InvoiceDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Modale de paiement
  const [paymentOpen, setPaymentOpen] = useState(false);

  // Édition de la note inline
  const [notesDraft, setNotesDraft] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesDirty, setNotesDirty] = useState(false);

  // Lot 4A — modale "ne plus relancer" + état "réactiver" inline
  const [disableReminderOpen, setDisableReminderOpen] = useState(false);
  const [remindersBusy, setRemindersBusy] = useState(false);

  // Section brouillon — édition projet/intervenant/montant + changement statut
  const [clients, setClients] = useState<ClientPublic[]>([]);
  const [projects, setProjects] = useState<ProjectPublic[]>([]);
  const [intervenants, setIntervenants] = useState<IntervenantPublic[]>([]);
  const [draftBusy, setDraftBusy] = useState(false);
  // champs du formulaire brouillon
  const [draftProjectId, setDraftProjectId] = useState<string>("");
  const [draftIntervenantId, setDraftIntervenantId] = useState<string>("");
  const [tenantVatRate, setTenantVatRate] = useState<number>(0);
  const [draftAmountHt, setDraftAmountHt] = useState<string>("");
  const [draftVatAmount, setDraftVatAmount] = useState<string>("");
  const [draftAmountTtc, setDraftAmountTtc] = useState<string>("");
  const [draftDueDate, setDraftDueDate] = useState<string>("");
  const [draftCurrency, setDraftCurrency] = useState<string>("");
  const [draftBillingMode, setDraftBillingMode] = useState<"full"|"percent"|"amount"|"qty">("full");
  const [draftPercent, setDraftPercent] = useState<string>("100");
  // Mode quantité : map lineId → qty à facturer
  const [lineQtys, setLineQtys] = useState<Record<string, string>>({});

  // Lot 5.2 — génération de lien de paiement Ziina
  const [paymentLinkBusy, setPaymentLinkBusy] = useState(false);
  const [paymentLinkError, setPaymentLinkError] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const paymentStatus = searchParams.get("payment");

  const reload = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await invoicesApi.get(id);
      setInvoice(data);
      setNotesDraft(data.notes ?? "");
      setNotesDirty(false);
      setDraftProjectId(data.project_id ?? "");
      setDraftIntervenantId(data.intervenant_id ?? "");
      setDraftAmountHt(data.amount_ht);
      setDraftVatAmount(data.vat_amount);
      setDraftAmountTtc(data.amount_ttc);
      setDraftDueDate(data.due_date);
        setDraftCurrency(data.currency ?? "EUR");
      // Init qtys avec les quantités du devis
      if (data.quotation_lines && data.quotation_lines.length > 0) {
        const init: Record<string, string> = {};
        data.quotation_lines.forEach((l: QuotationLine) => { init[l.id] = String(l.quantity); });
        setLineQtys(init);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Erreur de chargement";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void reload();
    // Charger les listes pour les sélecteurs du brouillon
    void clientsApi.list().then(setClients).catch(() => {});
    void projectsApi.list().then(setProjects).catch(() => {});
    void intervenantsApi.list().then(setIntervenants).catch(() => {});
    void companySettingsApi.get().then(s => setTenantVatRate(s.default_vat_rate ?? 0)).catch(() => {});
  }, [reload]);

  // Calcule automatiquement TTC depuis HT+TVA
  function recomputeTtc(ht: string, tva: string) {
    const h = parseFloat(ht) || 0;
    const t = parseFloat(tva) || 0;
    setDraftAmountTtc((h + t).toFixed(2));
  }

  async function saveDraft() {
    if (!invoice) return;
    setDraftBusy(true);
    try {
      let amountHt = draftAmountHt;
      let vatAmount = draftVatAmount;
      let amountTtc = draftAmountTtc;
      if (draftBillingMode === "percent") {
        const pct = parseFloat(draftPercent) / 100;
        amountHt = (parseFloat(invoice.amount_ht) * pct).toFixed(2);
        vatAmount = (parseFloat(invoice.vat_amount) * pct).toFixed(2);
        amountTtc = (parseFloat(invoice.amount_ttc) * pct).toFixed(2);
      } else if (draftBillingMode === "qty" && invoice.quotation_lines?.length) {
        let ht = 0; let ttc = 0;
        invoice.quotation_lines.forEach((l: QuotationLine) => {
          const qty = parseFloat(lineQtys[l.id] ?? l.quantity) || 0;
          const lineHt = qty * parseFloat(l.unit_price);
          const lineTtc = lineHt * (1 + parseFloat(l.vat_rate) / 100);
          ht += lineHt; ttc += lineTtc;
        });
        amountHt = ht.toFixed(2);
        vatAmount = (ttc - ht).toFixed(2);
        amountTtc = ttc.toFixed(2);
      }
      await invoicesApi.patch(invoice.id, {
        project_id: draftProjectId || null,
        intervenant_id: draftIntervenantId || null,
        currency: draftCurrency || undefined,
        amount_ht: amountHt,
        vat_amount: vatAmount,
        amount_ttc: amountTtc,
        due_date: draftDueDate || null,
      } as Parameters<typeof invoicesApi.patch>[1]);
      await reload();
    } catch (err) {
      window.alert(err instanceof ApiError ? err.detail : "Enregistrement impossible");
    } finally {
      setDraftBusy(false);
    }
  }

  async function changeStatus(newStatus: "issued" | "draft") {
    if (!invoice) return;
    if (newStatus === "issued" && !window.confirm("Valider et émettre cette facture ? Elle ne sera plus modifiable facilement.")) return;
    setDraftBusy(true);
    try {
      await invoicesApi.patch(invoice.id, { status: newStatus } as Parameters<typeof invoicesApi.patch>[1]);
      await reload();
    } catch (err) {
      window.alert(err instanceof ApiError ? err.detail : "Action impossible");
    } finally {
      setDraftBusy(false);
    }
  }

  async function saveNotes() {
    if (!invoice) return;
    setNotesSaving(true);
    try {
      await invoicesApi.patch(invoice.id, { notes: notesDraft || null });
      setNotesDirty(false);
      setDraftProjectId(data.project_id ?? "");
      setDraftIntervenantId(data.intervenant_id ?? "");
      setDraftAmountHt(data.amount_ht);
      setDraftVatAmount(data.vat_amount);
      setDraftAmountTtc(data.amount_ttc);
      setDraftDueDate(data.due_date);
        setDraftCurrency(data.currency ?? "EUR");
      // Init qtys avec les quantités du devis
      if (data.quotation_lines && data.quotation_lines.length > 0) {
        const init: Record<string, string> = {};
        data.quotation_lines.forEach((l: QuotationLine) => { init[l.id] = String(l.quantity); });
        setLineQtys(init);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Enregistrement impossible";
      window.alert(msg);
    } finally {
      setNotesSaving(false);
    }
  }

  // Lot 4A — "Ne plus relancer" : on patche la facture puis on recharge.
  async function disableReminders(reason: string) {
    if (!invoice) return;
    setRemindersBusy(true);
    try {
      await invoicesApi.patch(invoice.id, {
        reminders_disabled: true,
        reminders_disabled_reason: reason.trim() || null,
      });
      setDisableReminderOpen(false);
      await reload();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Action impossible";
      window.alert(msg);
    } finally {
      setRemindersBusy(false);
    }
  }

  async function enableReminders() {
    if (!invoice) return;
    setRemindersBusy(true);
    try {
      await invoicesApi.patch(invoice.id, {
        reminders_disabled: false,
        // On garde l'ancienne raison en base pour l'audit historique —
        // l'UI ne la ré-affichera plus puisque le flag est à false.
      });
      await reload();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Action impossible";
      window.alert(msg);
    } finally {
      setRemindersBusy(false);
    }
  }

  // Lot 5.2 — générer / régénérer un lien de paiement Ziina.
  async function createPaymentLink() {
    if (!invoice) return;
    setPaymentLinkBusy(true);
    setPaymentLinkError(null);
    try {
      await invoicesApi.createPaymentLink(invoice.id);
      await reload();
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.detail : "Impossible de générer le lien";
      setPaymentLinkError(msg);
    } finally {
      setPaymentLinkBusy(false);
    }
  }

  async function copyPaymentLink() {
    if (!invoice?.payment_link_url) return;
    try {
      await navigator.clipboard.writeText(invoice.payment_link_url);
      setCopyFeedback("Lien copié dans le presse-papiers");
      window.setTimeout(() => setCopyFeedback(null), 2500);
    } catch {
      setCopyFeedback("Copie impossible — copiez manuellement.");
    }
  }

  function dismissPaymentBanner() {
    // Retire le query param `payment` sans recharger la page.
    const next = new URLSearchParams(searchParams);
    next.delete("payment");
    setSearchParams(next, { replace: true });
  }

  async function openPdf() {
    if (!invoice?.invoice_upload_id) return;
    try {
      const { url } = await invoiceUploadsApi.downloadUrl(invoice.invoice_upload_id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "PDF indisponible";
      window.alert(msg);
    }
  }

  async function openGeneratedPdf(lang: "fr" | "en" = i18n.language === "en" ? "en" : "fr") {
    if (!invoice) return;
    try {
      const { blob, filename } = await invoicesApi.downloadPdf(invoice.id, lang);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename ?? ("facture-" + (invoice.reference ?? "facture") + ".pdf");
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "PDF indisponible";
      window.alert(msg);
    }
  }

  if (loading) {
    return (
      <AppShell>
        <p className="text-sm text-slate-500">{t("common.loading")}</p>
      </AppShell>
    );
  }
  if (error || !invoice) {
    return (
      <AppShell>
        <p role="alert" className="text-sm text-red-600">
          {error ?? t("invoiceDetail.notFound")}
        </p>
        <button
          type="button"
          className="mt-3 text-sm text-brand-600 hover:underline"
          onClick={() => navigate("/invoices")}
        >
          {t("invoiceDetail.backToList")}
        </button>
      </AppShell>
    );
  }

  const canEncash =
    invoice.effective_status !== "paid" &&
    invoice.effective_status !== "cancelled" &&
    invoice.effective_status !== "draft";

  async function onDelete() {
    if (
      !window.confirm(t("invoiceDetail.deleteConfirm"))
    ) {
      return;
    }
    try {
      await invoicesApi.delete(invoice.id);
      navigate("/invoices");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Suppression impossible";
      window.alert(msg);
    }
  }

  return (
    <AppShell>
      <div className="mb-4 flex items-center gap-3 text-sm">
        <Link to="/invoices" className="text-brand-600 hover:underline">
          {t("invoiceDetail.back")}
        </Link>
        <button
          type="button"
          onClick={() => void onDelete()}
          className="ml-auto text-sm text-red-500 hover:underline"
        >
          {t("invoiceDetail.deleteInvoice")}
        </button>
      </div>

      {/* Lot 5.2 — bannière retour de paiement Ziina (?payment=success|cancelled) */}
      {paymentStatus === "success" && (
        <div
          role="status"
          className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
        >
          <p>
            {t("invoiceDetail.paymentSuccess")}
          </p>
          <button
            type="button"
            className="text-emerald-700 hover:underline"
            onClick={dismissPaymentBanner}
          >
            {t("invoiceDetail.close")}
          </button>
        </div>
      )}
      {paymentStatus === "cancelled" && (
        <div
          role="status"
          className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          <p>{t("invoiceDetail.paymentCancelled")}</p>
          <button
            type="button"
            className="text-amber-700 hover:underline"
            onClick={dismissPaymentBanner}
          >
            {t("invoiceDetail.close")}
          </button>
        </div>
      )}

      <header className="mb-6 flex items-start justify-between gap-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            {invoice.reference}
          </h1>
          <p className="text-sm text-slate-500">
            {invoice.client_name}
            {invoice.project_name && ` · ${invoice.project_name}`}
            {invoice.intervenant_name && ` · ${invoice.intervenant_name}`}
          </p>
          <div className="mt-2 flex items-center gap-3">
            <DueBadge
              dueDate={invoice.due_date}
              effectiveStatus={invoice.effective_status}
            />
            <span className="text-xs text-slate-500">
              {t("invoiceDetail.issuedOn")} {formatDateI18n(invoice.issue_date)} · {t("invoiceDetail.dueOn")}{" "}
              {formatDateI18n(invoice.due_date)}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          {invoice.invoice_upload_id ? (
            <button type="button" className="btn-secondary" onClick={() => void openPdf()}>
              {t("invoiceDetail.downloadPdf")}
            </button>
          ) : (
            <div className="flex gap-2">
              <button type="button" className="btn-secondary" onClick={() => void openGeneratedPdf("fr")}>
                {t("invoiceDetail.downloadPdfFr")}
              </button>
              <button type="button" className="btn-secondary" onClick={() => void openGeneratedPdf("en")}>
                {t("invoiceDetail.downloadPdfEn")}
              </button>
            </div>
          )}
          {canEncash && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setPaymentOpen(true)}
            >
              {t("invoiceDetail.addPayment")}
            </button>
          )}
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs text-slate-500 uppercase">{t("invoiceDetail.totalTtc")}</p>
          <p className="text-xl font-semibold text-slate-900 mt-1">
            {formatAmount(invoice.amount_ttc, invoice.currency)}
          </p>
          <p className="text-xs text-slate-400">
            HT {formatAmount(invoice.amount_ht, invoice.currency)} · {t("invoiceDetail.paymentMethodOther").startsWith("O") ? "VAT" : "TVA"} ·{" "}
            {formatAmount(invoice.vat_amount, invoice.currency)}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs text-slate-500 uppercase">{t("invoiceDetail.collected")}</p>
          <p className="text-xl font-semibold text-emerald-600 mt-1">
            {formatAmount(invoice.total_paid, invoice.currency)}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs text-slate-500 uppercase">{t("invoiceDetail.remainingDue")}</p>
          <p className="text-xl font-semibold text-slate-900 mt-1">
            {formatAmount(invoice.remaining, invoice.currency)}
          </p>
        </div>
      </section>

      {/* Section brouillon — actions et édition */}
      {invoice.effective_status === "draft" && (
        <section className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
          <h2 className="text-base font-semibold text-amber-900 mb-1">{t("invoiceDetail.draftTitle")}</h2>
          <p className="text-xs text-amber-700 mb-4">Affectez un projet et un intervenant, ajustez le montant si besoin, puis validez.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Projet</label>
              <select value={draftProjectId} onChange={e => setDraftProjectId(e.target.value)} className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm">
                <option value="">— Aucun projet —</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Intervenant</label>
              <select value={draftIntervenantId} onChange={e => setDraftIntervenantId(e.target.value)} className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm">
                <option value="">— Aucun intervenant —</option>
                {intervenants.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Date d'échéance</label>
              <input type="date" value={draftDueDate} onChange={e => setDraftDueDate(e.target.value)} className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Devise</label>
              <CurrencySelect
                value={draftCurrency || invoice.currency}
                onChange={v => setDraftCurrency(v)}
                className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Facturation</label>
              <select value={draftBillingMode} onChange={e => setDraftBillingMode(e.target.value as "full"|"percent"|"amount")} className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm">
                <option value="full">Totalité du devis</option>
                <option value="percent">Pourcentage du devis</option>
                <option value="qty">Par quantité (lignes du devis)</option>
                <option value="amount">Montant personnalisé</option>
              </select>
            </div>
            {draftBillingMode === "qty" && invoice.quotation_lines && invoice.quotation_lines.length > 0 && (
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-700 mb-2">Quantités à facturer par ligne</label>
                <div className="space-y-2">
                  {invoice.quotation_lines.map((l: QuotationLine) => (
                    <div key={l.id} className="flex items-center gap-3 text-sm">
                      <span className="flex-1 text-slate-700">{l.description}</span>
                      <span className="text-xs text-slate-400">{l.unit_price} €/{l.unit ?? "u"} · TVA {l.vat_rate}%</span>
                      <input
                        type="number" min="0" max={l.quantity} step="0.001"
                        value={lineQtys[l.id] ?? l.quantity}
                        onChange={e => setLineQtys(prev => ({ ...prev, [l.id]: e.target.value }))}
                        className="w-24 rounded-md border border-slate-200 px-2 py-1 text-sm text-right"
                      />
                      <span className="text-xs text-slate-400">/ {l.quantity} {l.unit ?? ""}</span>
                      <span className="text-xs font-medium text-slate-700 w-24 text-right">
                        {((parseFloat(lineQtys[l.id] ?? l.quantity) || 0) * parseFloat(l.unit_price) * (1 + parseFloat(l.vat_rate) / 100)).toFixed(2)} €
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">Total TTC calculé : {invoice.quotation_lines.reduce((acc: number, l: QuotationLine) => acc + (parseFloat(lineQtys[l.id] ?? l.quantity) || 0) * parseFloat(l.unit_price) * (1 + parseFloat(l.vat_rate) / 100), 0).toFixed(2)} €</p>
              </div>
            )}
            {draftBillingMode === "percent" && (
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-700 mb-1">Pourcentage à facturer (%)</label>
                <input type="number" min="1" max="100" step="1" value={draftPercent} onChange={e => setDraftPercent(e.target.value)} className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm" />
                <p className="text-xs text-slate-500 mt-1">TTC calculé : {((parseFloat(invoice.amount_ttc)||0) * (parseFloat(draftPercent)||0) / 100).toFixed(2)} {invoice.currency}</p>
              </div>
            )}
            {draftBillingMode === "amount" && (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Montant HT (€)</label>
                  <input type="number" step="0.01" value={draftAmountHt} onChange={e => {
                    const ht = parseFloat(e.target.value) || 0;
                    const origHt = parseFloat(invoice.amount_ht) || 1;
                    const existingRate = parseFloat(invoice.vat_amount) / origHt;
                    // Utilise le taux configuré dans Company Settings si la facture n'a pas de TVA
                    const vatRate = existingRate > 0 ? existingRate : tenantVatRate / 100;
                    const tva = (ht * vatRate).toFixed(2);
                    setDraftAmountHt(e.target.value);
                    setDraftVatAmount(tva);
                    setDraftAmountTtc((ht + parseFloat(tva)).toFixed(2));
                  }} className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">TVA (€)</label>
                  <input type="number" step="0.01" value={draftVatAmount} onChange={e => { setDraftVatAmount(e.target.value); setDraftAmountTtc(((parseFloat(draftAmountHt)||0) + (parseFloat(e.target.value)||0)).toFixed(2)); }} className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-slate-700 mb-1">Total TTC (€)</label>
                  <input type="number" step="0.01" value={draftAmountTtc} onChange={e => setDraftAmountTtc(e.target.value)} className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm" />
                </div>
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => void saveDraft()} disabled={draftBusy} className="btn-secondary disabled:opacity-50">
              {draftBusy ? "Enregistrement…" : "Enregistrer les modifications"}
            </button>
            <button type="button" onClick={() => void changeStatus("issued")} disabled={draftBusy} className="btn-primary disabled:opacity-50">
              ✓ Valider et émettre la facture
            </button>
          </div>
        </section>
      )}
      {/* Bouton retour en brouillon si issued */}
      {invoice.effective_status === "issued" && (
        <div className="mb-4 flex justify-end">
          <button type="button" onClick={() => void changeStatus("draft")} disabled={draftBusy} className="text-sm text-slate-500 hover:underline disabled:opacity-50">
            ↩ Remettre en brouillon
          </button>
        </div>
      )}
      <PaymentLinkSection
        invoice={invoice}
        busy={paymentLinkBusy}
        canCreate={canEncash}
        error={paymentLinkError}
        copyFeedback={copyFeedback}
        onCreate={() => void createPaymentLink()}
        onCopy={() => void copyPaymentLink()}
      />

      <section className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
        <h2 className="text-base font-semibold text-slate-900 mb-2">{t("common.notes")}</h2>
        <textarea
          value={notesDraft}
          onChange={(e) => {
            setNotesDraft(e.target.value);
            setNotesDirty(true);
          }}
          placeholder={t("invoiceDetail.notesPlaceholder")}
          className="w-full min-h-[80px] rounded-md border border-slate-200 px-3 py-2 text-sm"
          aria-label="Note de la facture"
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            className="btn-primary disabled:opacity-50"
            disabled={!notesDirty || notesSaving}
            onClick={() => void saveNotes()}
          >
            {notesSaving ? t("invoiceDetail.saving") : t("invoiceDetail.saveNote")}
          </button>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <header className="px-4 py-3 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-900">
            {t("invoiceDetail.paymentsHistory")}
          </h2>
        </header>
        {invoice.payments.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">
            {t("invoiceDetail.noPayments")}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">{t("invoiceDetail.colDate")}</th>
                <th className="px-4 py-2 font-medium">{t("invoiceDetail.colMethod")}</th>
                <th className="px-4 py-2 font-medium">{t("invoiceDetail.colRef")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("invoiceDetail.colAmount")}</th>
              </tr>
            </thead>
            <tbody>
              {invoice.payments.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-600">
                    {formatDateI18n(p.payment_date)}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {getMethodLabels(t)[p.method]}
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {p.provider_ref ?? "—"}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-900 font-medium">
                    {formatAmount(p.amount, p.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <RemindersSection
        reminders={invoice.reminders}
        remindersDisabled={invoice.reminders_disabled}
        disabledReason={invoice.reminders_disabled_reason}
        disabledAt={invoice.reminders_disabled_at}
        busy={remindersBusy}
        canToggle={canEncash /* pas de relance si draft/paid/cancelled */}
        onRequestDisable={() => setDisableReminderOpen(true)}
        onReenable={() => void enableReminders()}
      />

      <Modal
        open={disableReminderOpen}
        title={t("invoiceDetail.disableReminderTitle")}
        size="sm"
        onClose={() => !remindersBusy && setDisableReminderOpen(false)}
      >
        <DisableRemindersForm
          submitting={remindersBusy}
          onCancel={() => setDisableReminderOpen(false)}
          onSubmit={(reason) => void disableReminders(reason)}
        />
      </Modal>

      <Modal
        open={paymentOpen}
        title={t("invoiceDetail.addPayment")}
        onClose={() => setPaymentOpen(false)}
      >
        <PaymentForm
          max={invoice.remaining}
          currency={invoice.currency}
          onCancel={() => setPaymentOpen(false)}
          onSubmit={async (payload) => {
            try {
              await invoicesApi.addPayment(invoice.id, payload);
              setPaymentOpen(false);
              await reload();
            } catch (err) {
              const msg =
                err instanceof ApiError ? err.detail : "Paiement impossible";
              window.alert(msg);
            }
          }}
        />
      </Modal>
    </AppShell>
  );
}

// ---- Sous-composant : formulaire d'encaissement ----------------------------

type PaymentFormProps = {
  max: string;
  currency: string;
  onCancel: () => void;
  onSubmit: (payload: PaymentCreatePayload) => Promise<void>;
};

function PaymentForm({ max, currency, onCancel, onSubmit }: PaymentFormProps) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(max);
  const [paymentDate, setPaymentDate] = useState(todayISO());
  const [method, setMethod] = useState<PaymentMethod>("bank_transfer");
  const [providerRef, setProviderRef] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        amount: amount || null,
        payment_date: paymentDate || null,
        method,
        provider_ref: providerRef || null,
        note: note || null,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4"
      aria-label="Formulaire d'encaissement"
    >
      <FormField label={`${t("invoiceDetail.paymentAmount")} (${currency})`} htmlFor="amount" required>
        <input
          id="amount"
          type="number"
          step="0.01"
          min="0.01"
          max={max}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm"
          required
        />
      </FormField>
      <FormField label={t("invoiceDetail.paymentDate")} htmlFor="payment_date" required>
        <input
          id="payment_date"
          type="date"
          value={paymentDate}
          onChange={(e) => setPaymentDate(e.target.value)}
          className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm"
          required
        />
      </FormField>
      <FormField label={t("invoiceDetail.paymentMethod")} htmlFor="method">
        <select
          id="method"
          value={method}
          onChange={(e) => setMethod(e.target.value as PaymentMethod)}
          className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm"
        >
          {(Object.keys(getMethodLabels(t)) as PaymentMethod[]).map((m) => (
            <option key={m} value={m}>
              {getMethodLabels(t)[m]}
            </option>
          ))}
        </select>
      </FormField>
      <FormField
        label={t("invoiceDetail.paymentRef")}
        htmlFor="provider_ref"
      >
        <input
          id="provider_ref"
          type="text"
          value={providerRef}
          onChange={(e) => setProviderRef(e.target.value)}
          className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm"
        />
      </FormField>
      <FormField label={t("invoiceDetail.paymentNote")} htmlFor="note">
        <textarea
          id="note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full min-h-[60px] rounded-md border border-slate-200 px-3 py-1.5 text-sm"
        />
      </FormField>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="text-sm text-slate-500 hover:underline"
          onClick={onCancel}
        >
          {t("invoiceDetail.paymentCancel")}
        </button>
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? t("invoiceDetail.paymentSaving") : t("invoiceDetail.paymentConfirm")}
        </button>
      </div>
    </form>
  );
}

// ---- Sous-composant : section relances (Lot 4A) ----------------------------
//
// Montre :
//  - L'état "ne plus relancer" (avec raison + date) quand le flag est actif,
//    et propose de le retirer.
//  - Sinon, un bouton pour désactiver les relances.
//  - Un historique des relances déjà envoyées (ordonné plus récent d'abord).
//
// On délègue les actions au parent pour que l'état reste colocalisé avec
// le reste du détail (reload, busy, etc.).

type RemindersSectionProps = {
  reminders: ReminderPublic[];
  remindersDisabled: boolean;
  disabledReason: string | null;
  disabledAt: string | null;
  busy: boolean;
  canToggle: boolean;
  onRequestDisable: () => void;
  onReenable: () => void;
};

function RemindersSection({
  reminders,
  remindersDisabled,
  disabledReason,
  disabledAt,
  busy,
  canToggle,
  onRequestDisable,
  onReenable,
}: RemindersSectionProps) {
  const { t } = useTranslation();
  const REMINDER_STATUS_LABEL = getReminderStatusLabels(t);
  return (
    <section
      aria-label="Relances"
      className="bg-white border border-slate-200 rounded-xl overflow-hidden mt-6"
    >
      <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{t("invoiceDetail.remindersTitle")}</h2>
          <p className="text-xs text-slate-500">{t("invoiceDetail.remindersSubtitle")}</p>
        </div>
        {/* Toggle d'action — masqué pour les statuts non relançables */}
        {canToggle &&
          (remindersDisabled ? (
            <button
              type="button"
              className="text-sm text-brand-600 hover:underline disabled:opacity-50"
              onClick={onReenable}
              disabled={busy}
            >
              Réactiver les relances
            </button>
          ) : (
            <button
              type="button"
              className="btn-secondary"
              onClick={onRequestDisable}
              disabled={busy}
            >
              Ne plus relancer
            </button>
          ))}
      </header>

      {remindersDisabled && (
        <div
          role="status"
          className="px-4 py-3 bg-amber-50 border-b border-amber-100 text-sm text-amber-800"
        >
          <strong className="font-medium">{t("invoiceDetail.remindersDisabled")}</strong>
          {disabledAt && (
            <span className="text-amber-700">
              {" "}
              le {formatDateTimeI18n(disabledAt)}
            </span>
          )}
          {disabledReason && (
            <p className="mt-1 text-amber-700">{t("invoiceDetail.remindersReason")} {disabledReason}</p>
          )}
        </div>
      )}

      {reminders.length === 0 ? (
        <p className="p-4 text-sm text-slate-500">{t("invoiceDetail.noReminders")}</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">{t("invoiceDetail.colDate")}</th>
              <th className="px-4 py-2 font-medium">{t("invoiceDetail.colRecipient")}</th>
              <th className="px-4 py-2 font-medium">{t("invoiceDetail.colTemplate")}</th>
              <th className="px-4 py-2 font-medium">{t("invoiceDetail.colStatus")}</th>
            </tr>
          </thead>
          <tbody>
            {reminders.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 align-top">
                <td className="px-4 py-2 text-slate-600 whitespace-nowrap">
                  {formatDateTimeI18n(r.sent_at)}
                </td>
                <td className="px-4 py-2 text-slate-700">{r.recipient_email}</td>
                <td className="px-4 py-2 text-slate-600">{r.template_key}</td>
                <td className="px-4 py-2">
                  <span
                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${REMINDER_STATUS_CLS[r.status]}`}
                  >
                    {REMINDER_STATUS_LABEL[r.status]}
                  </span>
                  {r.status === "failed" && r.error_message && (
                    <p className="mt-1 text-xs text-red-600">
                      {r.error_message}
                    </p>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ---- Sous-composant : lien de paiement en ligne (Lot 5.2) -----------------
//
// Affiche la carte "Lien de paiement" entre les KPIs et les notes.
//
// - Aucun lien : bouton "Générer un lien Ziina" (disabled si la facture
//   n'est pas encaissable — draft, paid, cancelled).
// - Lien existant : URL affichée, infos de création/expiration, boutons
//   "Copier le lien" et "Régénérer le lien" (le régénère écrase l'ancien).
// - Erreurs provider remontées inline (502 = provider indisponible,
//   503 = Ziina non configuré).

type PaymentLinkSectionProps = {
  invoice: InvoiceDetail;
  busy: boolean;
  canCreate: boolean;
  error: string | null;
  copyFeedback: string | null;
  onCreate: () => void;
  onCopy: () => void;
};

function PaymentLinkSection({
  invoice,
  busy,
  canCreate,
  error,
  copyFeedback,
  onCreate,
  onCopy,
}: PaymentLinkSectionProps) {
  const { t } = useTranslation();
  const url = invoice.payment_link_url;
  const createdAt = invoice.payment_link_created_at;
  const expiresAt = invoice.payment_link_expires_at;
  const isExpired =
    expiresAt !== null && new Date(expiresAt).getTime() < Date.now();

  return (
    <section
      aria-label="Lien de paiement en ligne"
      className="bg-white border border-slate-200 rounded-xl p-4 mb-6"
    >
      <header className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{t("invoiceDetail.paymentLinkTitle")}</h2>
          <p className="text-xs text-slate-500">
            {t("invoiceDetail.paymentLinkDesc")}
          </p>
        </div>
        {!url && canCreate && (
          <button
            type="button"
            className="btn-primary disabled:opacity-50"
            onClick={onCreate}
            disabled={busy}
          >
            {busy ? t("invoiceDetail.generating") : t("invoiceDetail.generateZiina")}
          </button>
        )}
      </header>

      {!canCreate && !url && (
        <p className="text-sm text-slate-500">{t("invoiceDetail.paymentLinkNotAvailable")}</p>
      )}

      {url && (
        <div className="space-y-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-xs text-slate-500">{t("invoiceDetail.paymentLinkUrl")}</p>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="break-all text-sm text-brand-600 hover:underline"
            >
              {url}
            </a>
          </div>
          <dl className="grid grid-cols-1 gap-1 text-xs text-slate-500 sm:grid-cols-2">
            {createdAt && (
              <div>
                <dt className="inline">{t("invoiceDetail.paymentLinkGeneratedAt")} </dt>
                <dd className="inline text-slate-700">
                  {formatDateTimeI18n(createdAt)}
                </dd>
              </div>
            )}
            {expiresAt && (
              <div>
                <dt className="inline">{t("invoiceDetail.paymentLinkExpiresAt")} </dt>
                <dd
                  className={
                    isExpired
                      ? "inline text-red-600 font-medium"
                      : "inline text-slate-700"
                  }
                >
                  {formatDateTimeI18n(expiresAt)}
                  {isExpired && " " + t("invoiceDetail.paymentLinkExpired")}
                </dd>
              </div>
            )}
          </dl>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={onCopy}
              disabled={busy}
            >
              Copier le lien
            </button>
            {canCreate && (
              <button
                type="button"
                className="text-sm text-brand-600 hover:underline disabled:opacity-50"
                onClick={onCreate}
                disabled={busy}
              >
                {busy ? t("invoiceDetail.regenerating") : t("invoiceDetail.regenerateZiina")}
              </button>
            )}
            {copyFeedback && (
              <span
                role="status"
                className="text-xs text-emerald-700"
              >
                {copyFeedback}
              </span>
            )}
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {error}
        </p>
      )}
    </section>
  );
}

// ---- Sous-composant : formulaire "ne plus relancer" ------------------------

type DisableRemindersFormProps = {
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
};

function DisableRemindersForm({
  submitting,
  onCancel,
  onSubmit,
}: DisableRemindersFormProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(reason);
      }}
      className="space-y-4"
      aria-label="Désactiver les relances"
    >
      <p className="text-sm text-slate-600">{t("invoiceDetail.disableReminderDesc")}</p>
      <FormField
        label={t("invoiceDetail.disableReminderReason")}
        htmlFor="reminders_disabled_reason"
      >
        <textarea
          id="reminders_disabled_reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("invoiceDetail.disableReminderReasonPlaceholder")}
          maxLength={500}
          className="w-full min-h-[80px] rounded-md border border-slate-200 px-3 py-1.5 text-sm"
        />
      </FormField>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="text-sm text-slate-500 hover:underline"
          onClick={onCancel}
          disabled={submitting}
        >
          {t("invoiceDetail.paymentCancel")}
        </button>
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? t("invoiceDetail.saving") : t("invoiceDetail.disableReminderConfirm")}
        </button>
      </div>
    </form>
  );
}
